import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { fetchCurrentUser } from '../../ducks/user.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import {
  publishDraftWithoutForumBatch,
  publishSelectedWithForumBatch as publishSelectedWithForumBatchUtil,
} from '../../util/listingImport/clearForumPublishBatch';
import { FORUM_PUBLISH_BATCH_KEY } from '../../util/listingImport/constants';
import {
  listingCardImageFields,
  listingCardImageVariantParams,
} from '../../util/listingImageVariants';
import { LISTING_STATE_DRAFT } from '../../util/types';

const queryDraftsPage = async (sdk, page, listingImageConfig) => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    listingImageConfig || {};
  const aspectRatio = aspectHeight / aspectWidth;
  return sdk.ownListings.query({
    states: [LISTING_STATE_DRAFT],
    page,
    perPage: 100,
    include: ['images', 'currentStock'],
    'fields.image': listingCardImageFields(variantPrefix),
    ...listingCardImageVariantParams(variantPrefix, aspectRatio),
  });
};

export const queryFinishDraftsThunk = createAsyncThunk(
  'app/FinishImportedListingsPage/queryDrafts',
  async ({ config } = {}, { extra: sdk, rejectWithValue }) => {
    try {
      const listingImageConfig = config?.layout?.listingImage;
      const listings = [];
      let page = 1;
      let totalPages = 1;
      do {
        const response = await queryDraftsPage(sdk, page, listingImageConfig);
        listings.push(...denormalisedResponseEntities(response));
        totalPages = Number(response?.data?.meta?.totalPages || 1) || 1;
        page += 1;
      } while (page <= totalPages);
      return listings;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export { FORUM_PUBLISH_BATCH_KEY };

export const publishSelectedWithForumBatch = publishSelectedWithForumBatchUtil;

export const publishSelectedWithForumBatchThunk = createAsyncThunk(
  'app/FinishImportedListingsPage/publishSelectedWithForumBatch',
  async ({ listings } = {}, { extra: sdk, rejectWithValue }) => {
    try {
      return await publishSelectedWithForumBatch({ sdk, listings });
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const publishImportedListingThunk = createAsyncThunk(
  'app/FinishImportedListingsPage/publish',
  async ({ listingId }, { extra: sdk, rejectWithValue }) => {
    try {
      return await publishDraftWithoutForumBatch(sdk, listingId, { expand: true });
    } catch (e) {
      return rejectWithValue({ listingId, ...storableError(e) });
    }
  }
);

export const discardImportedListingThunk = createAsyncThunk(
  'app/FinishImportedListingsPage/discard',
  async ({ listingId }, { extra: sdk, rejectWithValue }) => {
    try {
      await sdk.ownListings.discardDraft({ id: listingId }, { expand: true });
      return listingId?.uuid || listingId;
    } catch (e) {
      return rejectWithValue({ listingId, ...storableError(e) });
    }
  }
);

const slice = createSlice({
  name: 'FinishImportedListingsPage',
  initialState: {
    listings: [],
    queryInProgress: false,
    queryError: null,
    publishInProgress: false,
    publishErrors: [],
  },
  reducers: {
    setPublishErrors: (state, action) => {
      state.publishErrors = action.payload || [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(queryFinishDraftsThunk.pending, state => {
        state.queryInProgress = true;
        state.queryError = null;
      })
      .addCase(queryFinishDraftsThunk.fulfilled, (state, action) => {
        state.queryInProgress = false;
        state.listings = action.payload || [];
      })
      .addCase(queryFinishDraftsThunk.rejected, (state, action) => {
        state.queryInProgress = false;
        state.queryError = action.payload;
      })
      .addCase(publishImportedListingThunk.pending, state => {
        state.publishInProgress = true;
      })
      .addCase(publishImportedListingThunk.fulfilled, (state, action) => {
        state.publishInProgress = false;
        const id = action.meta?.arg?.listingId?.uuid || action.meta?.arg?.listingId;
        const idStr = id?.uuid || id;
        state.listings = state.listings.filter(l => l.id?.uuid !== idStr);
      })
      .addCase(publishImportedListingThunk.rejected, state => {
        state.publishInProgress = false;
      })
      .addCase(publishSelectedWithForumBatchThunk.pending, state => {
        state.publishInProgress = true;
      })
      .addCase(publishSelectedWithForumBatchThunk.fulfilled, (state, action) => {
        state.publishInProgress = false;
        const published = new Set(action.payload?.publishedIds || []);
        state.listings = state.listings.filter(l => !published.has(l.id?.uuid || l.id));
        state.publishErrors = action.payload?.errors || [];
      })
      .addCase(publishSelectedWithForumBatchThunk.rejected, state => {
        state.publishInProgress = false;
      })
      .addCase(discardImportedListingThunk.fulfilled, (state, action) => {
        const idStr = action.payload;
        state.listings = state.listings.filter(l => l.id?.uuid !== idStr);
      });
  },
});

export const { setPublishErrors } = slice.actions;

export const loadData = (params, search, config) => dispatch => {
  return dispatch(fetchCurrentUser()).then(() => dispatch(queryFinishDraftsThunk({ config })));
};

export default slice.reducer;
